import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import { ApiConstant, CodeConstant } from '../../../../constants';
import { Typography, Alert, Select, InputNumber } from 'antd';
import { pushLog2DV2LogServer } from '../../../../services/logging';
import { debounce, isEqual, cloneDeep } from 'lodash';

import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import SkillEditorActions from './SkillEditor.action';
import CancelIcon from '@material-ui/icons/Cancel';
import { HeaderErrorIcon } from '../../../components/common/CustomIcon';
import './SkillEditor.css';
import { GB_FLAGS, withLDFlagsFromBackend } from '../../../services/launchDarkly';

const { Text } = Typography;
const { Option } = Select;
const { setUserOtherSkills, setValidationError } = SkillEditorActions;

const SKILL_LEVELS = {
  1: 'Beginner',
  2: 'Experienced',
  3: 'Advanced',
  4: 'Expert'
};

const levelMapping = {
  beginner: 'I do not have any professional experience in this skill.',
  experienced: 'I have used this skill professionally in a limited capacity.',
  advanced: ' I have used this skill professionally at least once a week (example: deployment).',
  expert: 'I have used this skill professionally on a daily basis.'
};

const Spin = ({ status, onChangeStatus }) => {
  const handleChangeStatus = () => {
    if (status < 1) { onChangeStatus(1); } else onChangeStatus(-1);
  };

  return (
    <div className="spin-container" onClick={handleChangeStatus}>
      <ArrowDropUp className="arrow-up" style={{ color: (status === -1 && '#1890ff') }}/>
      <ArrowDropDown className="arrow-down" style={{ color: (status === 1 && '#1890ff') }}/>
    </div>
  );
};

Spin.propTypes = {
  status: PropTypes.number.isRequired,
  onChangeStatus: PropTypes.func.isRequired
};

class SkillEditor extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      allSkills: [],
      baseSkills: [],
      message_status: 0,
      newSkill: {},
      unmounted: true,
      allSkillsExpUpdatedIdx: null
    };
    this.debounceApiFuncs = {};

    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common.Authorization = token;
    }

    this.debounceUpdateSkillData = debounce(this.updateSkillData, 500);
    this.debounceAddSkillData = debounce(this.addSkillData, 500);
    this.debounceDeleteSkillData = debounce(this.deleteSkillData, 500);
    this.user_demand_stack_skills = cloneDeep(this.props.user_demand_stack_skills);
  }

  componentDidMount () {
    this.getDeveloperSkillInfo();
    this.getBaseAllSkillList();
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    if (!isEqual(this.user_demand_stack_skills, nextProps.user_demand_stack_skills)) {
      this.handleStackDemandSkillUpdate(this.user_demand_stack_skills.skillsIds, nextProps.user_demand_stack_skills.skillsIds);
      this.user_demand_stack_skills = cloneDeep(nextProps.user_demand_stack_skills);
    }
  }

  componentDidUpdate (prevProps) {
    if (this.props.LDFlags.$flagsReady
      && (!isEqual(prevProps.user_demand_stack_skills, this.props.user_demand_stack_skills) 
      || !isEqual(prevProps.LDFlags.$flagsReady, this.props.LDFlags.$flagsReady))) {
      this.getDeveloperSkillInfo();
  }
    if ((prevProps.LDFlags.$flagsReady !== this.props.LDFlags.$flagsReady) && this.props.LDFlags.$flagsReady) {
      this.validate();
    }
  }

  // Set skill level for new onboarding improved ui ux flag user
  autoSetSkillLevel = (skill_data) => {
    return skill_data.map(item => {
      if (!item.skill_level) {
        item.skill_level = 1
      };
      return item;
    })
  }

  handleStackDemandSkillUpdate = async (oldStackDemandSkills, newStackDemandSkills) => {
    const { LDFlags } = this.props;
    const { use_onboarding_improved_ui_ux_multi_variant } = LDFlags;
    try {
      const addedSkills = newStackDemandSkills.filter(id => !oldStackDemandSkills.includes(id));
      const removedSkills = oldStackDemandSkills.filter(id => !newStackDemandSkills.includes(id));
      const { user_demand_stack_skills } = this.props;
      const response = await axios.put(`${ApiConstant.BASE_URL}${ApiConstant.UPDATE_USER_DEMAND_STACK_SKILLS}`, {
        addedSkills: addedSkills,
        removedSkills: removedSkills
      });
      if (response.data.code === CodeConstant.OK) {
        const { all_skills } = response.data.data;
        let skill_data = all_skills || [];
  
        if (use_onboarding_improved_ui_ux_multi_variant === 'v1') {
          skill_data = skill_data.slice()
        } else if (use_onboarding_improved_ui_ux_multi_variant === 'v2') {
          skill_data = this.autoSetSkillLevel(skill_data.slice())
        }
        const data = {
          allSkills: skill_data,
        };

        const otherSkills = all_skills.filter(skill => !user_demand_stack_skills.skillsIds.includes(skill.skill_id)).map((skill) => skill.skill_id);

        if (this.props.handleAllSkills) {
          this.props.handleAllSkills(data, true);
        }

        if (this.state.unmounted) {
          this.setState({ ...data }, this.validate);
          this.props.setUserOtherSkills(otherSkills);
        }

        pushLog2DV2LogServer.info('USER_PROFILE', 'UPDATE_USER_DEMAND_STACK_SKILLS_SUCCESS', {}, data, null);
      } else {
        pushLog2DV2LogServer.warn('USER_PROFILE', 'UPDATE_USER_DEMAND_STACK_SKILLS_FAILED', {}, {}, {});
      }
    } catch (e) {
      pushLog2DV2LogServer.error('USER_PROFILE', 'UPDATE_USER_DEMAND_STACK_SKILLS_FAILED', {}, {}, e);
    }
  }

  componentWillUnmount () {
    this.setState({ unmounted: false });
  }

  validate () {
    const { allSkills=[], newSkill } = this.state;

    this.stillGotError = allSkills.some(skill => !this.isSkillDataCompleted(skill)) || !this.isSkillDataCompleted(newSkill);
    const message = this.stillGotError ? 'Please fill skill missing values.' : '';
    this.props.setValidationError({ message });
  }

  isSkillDataCompleted (skill) {
    if (!skill.skill_id) return true; /* ONLY VALIDATE FOR SKILL WITH skill_id */
    return skill.skill_id && skill.score && skill.skill_level;
  }

  getDeveloperSkillInfo = async () => {
    const { user_demand_stack_skills, LDFlags } = this.props;
    const { use_onboarding_improved_ui_ux_multi_variant } = LDFlags;

    try {
      const response = await axios.get(`${ApiConstant.BASE_URL}${ApiConstant.GET_DEVELOPER_SKILL_INFO}`);

      if (response.data.code === CodeConstant.OK) {
        const { all_skills } = response.data.data;

        let skill_data = all_skills || [];
        if (use_onboarding_improved_ui_ux_multi_variant === 'v1') {
          skill_data = skill_data.slice()
        } else if (use_onboarding_improved_ui_ux_multi_variant === 'v2') {
          skill_data = this.autoSetSkillLevel(skill_data.slice())
        }
        const data = {
          allSkills: skill_data,
        };

        const otherSkills = all_skills.filter(skill => !user_demand_stack_skills.skillsIds.includes(skill.skill_id)).map((skill) => skill.skill_id);

        if (this.props.handleAllSkills) {
          this.props.handleAllSkills(data, true);
        }

        if (this.state.unmounted) {
          this.setState({ ...data }, this.validate);
          this.props.setUserOtherSkills(otherSkills);
        }
        pushLog2DV2LogServer.info('USER_PROFILE', 'GET_DEVELOPER_SKILL_INFO_SUCCESS', {}, data, null);
      }
    } catch (e) {
      console.log(e);
      pushLog2DV2LogServer.error('USER_PROFILE', 'GET_DEVELOPER_SKILL_INFO_FAILED', {}, {}, e);
    }
    if (this.props.onReady) this.props.onReady();
  }

  getBaseAllSkillList = async () => {
    try {
      const body = {
        action: 'GET_BASE_ALL_SKILL_LIST',
        get_canonical_skills: true
      };
      const response = await axios.post(`${ApiConstant.BASE_URL}common/`, body);
      if (response.data.success) {
        if (this.state.unmounted) this.setState({ baseSkills: response.data.result });
        const data = response.data.result;
        pushLog2DV2LogServer.info('USER_PROFILE', 'GET_STACKS_DEMAND_LIST_SUCCESS', {}, data, null);
      }
    } catch (e) {
      pushLog2DV2LogServer.error('USER_PROFILE', 'GET_STACKS_DEMAND_LIST', {}, {}, e);
    }
  };

  saveAllSkillUpdatedExperience = () => {
    // method to update all skills experience entry if it is changed
    const { allSkills, allSkillsExpUpdatedIdx = null } = this.state;
    if (allSkillsExpUpdatedIdx !== null) { // doing this null check since 0 might come as index value
      this.setState({ ...this.state, allSkillsExpUpdatedIdx: null });
      this.updateSkillData(allSkills, allSkillsExpUpdatedIdx);
    }
  }

  handleAllSkillInfoChange = async (skillId, key, value) => {
    if (!value) return; /* CRASH IF SCORE VALUE = "" NOT NUMBER */

    const { allSkills, baseSkills } = this.state;
    const index = allSkills.findIndex(element => element.id + '' === skillId + '');

    if (allSkills[index][key] === value) return; // returning if the value is not changed

    allSkills[index][key] = value;

    this.setState({ allSkills: [...allSkills] }, this.validate);
    if (key === 'score') { // update skill API is fired only on blur action for experience input and not on each change
      this.setState({ ...this.state, allSkillsExpUpdatedIdx: index });
      return;
    }
    if (this.isSkillDataCompleted(allSkills[index])) {
      this.debounceUpdateSkillData(allSkills, index);
    }
  }

  validateSkillExperienceInput = (event) => {
    // Allow only numbers, left arrow, right arrow, backspace & delete keys inside input box
    let allow = false;
    if (event.key) {
      const key = event.key;
      const allowed_keys = ['Left', 'ArrowLeft', 'Right', 'ArrowRight', 'Backspace', 'Delete'];

      if (allowed_keys.includes(key) || (key !== ' ' && !isNaN(key))) allow = true;
    } else if (event.keyCode || event.which) {
      const key = event.keyCode || event.which;
      // keycode for backspace - 8, Delete - 46, left arrow - 37, right arrow - 39
      // numbers 0 to 9 - 48 to 57 & for numeric keypad - 96 to 105
      if (key === 8 || key === 46 || key === 37 || key === 39 ||
        (key >= 48 && key <= 57) || (key >= 96 && key <= 105)) {
        allow = true;
      }
    } else {
      allow = true; // this will execute only when event.key, event.keyCode & event.which returns null
    }

    if (!allow) event.preventDefault();
  };

  handleAddAllSkill = async (key, value) => {
    const { baseSkills, allSkills, newSkill } = this.state;
    const { LDFlags } = this.props;
    const { use_onboarding_improved_ui_ux_multi_variant } = LDFlags;
    const item = { ...newSkill, [key]: value };

    if (item.skill_id) {
      const base_skill = baseSkills.find((element) => element.id === item.skill_id);
      item.skill_name = base_skill.skill_name;

      if (use_onboarding_improved_ui_ux_multi_variant === 'v2' && !item.skill_level) {
        item.skill_level = 1
      }
    }

    this.setState({ newSkill: item }, this.validate);

    if (this.isSkillDataCompleted(item)) {
      const temp_allSkills = allSkills.slice();
      temp_allSkills.push(item);
      this.setState({ allSkills: [...allSkills] });

      this.debounceAddSkillData(temp_allSkills, temp_allSkills.length - 1);

      let skill_level = '';
      if (use_onboarding_improved_ui_ux_multi_variant === 'v2') {
        skill_level = 1;
      }

        this.setState({
          newSkill: {
            skill_id: '',
            skill_name: '',
            score: '',
            skill_level
          }
        });
    }
  }

  handleDeleteAllSkill = async (skillId) => {
    const { allSkills } = this.state;

    const index = allSkills.findIndex(element => element.id + '' === skillId + '');

    const temp_allSkills = allSkills.slice();
    temp_allSkills.splice(index, 1);
    this.setState({ allSkills: [...temp_allSkills] }, this.validate);
    this.debounceDeleteSkillData(temp_allSkills, skillId);
  }

  updateSkillData = async (allSkills, index) => {
    try {
      const { user_demand_stack_skills } = this.props;

      const response = await axios.put(`${ApiConstant.BASE_URL}${ApiConstant.UPDATE_USER_SKILL_DATA}`, {
        skill: allSkills[index]
      });
      if (response.data.code === CodeConstant.OK) {
        const data = {
          allSkills: allSkills.slice()
        };

        const otherSkills = allSkills.filter(skill => !user_demand_stack_skills.skillsIds.includes(skill.skill_id)).map((skill) => skill.skill_id);

        if (this.props.handleAllSkills) {
          this.props.handleAllSkills(data, true);
        }

        if (this.state.unmounted) {
          this.setState({ ...data });
          this.props.setUserOtherSkills(otherSkills);
        }

        pushLog2DV2LogServer.info('USER_PROFILE', 'UPDATE_USER_SKILLS_SUCCESS', {}, data, null);
      } else {
        pushLog2DV2LogServer.warn('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, { code: response.data.code });
      }
    } catch (e) {
      pushLog2DV2LogServer.error('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, e);
    }
  }

  addSkillData = async (allSkills, index) => {
    const { user_demand_stack_skills } = this.props;
    try {
      const skill = allSkills[index];
      if (skill.skill_id) { // Only make API call when there is skill ID
        const response = await axios.post(`${ApiConstant.BASE_URL}${ApiConstant.ADD_USER_SKILL_DATA}`, {
          skill: allSkills[index]
        });
        if (response.data.code === CodeConstant.OK) {
          allSkills[index] = {
            ...allSkills[index],
            ...response.data.data
          };
          const data = {
            allSkills: allSkills.slice()
          };
  
          const otherSkills = allSkills.filter(skill => !user_demand_stack_skills.skillsIds.includes(skill.skill_id)).map((skill) => skill.skill_id);
  
          if (this.props.handleAllSkills) {
            this.props.handleAllSkills(data, true);
          }
  
          if (this.state.unmounted) {
            this.setState({ ...data });
            this.props.setUserOtherSkills(otherSkills);
          }
          pushLog2DV2LogServer.info('USER_PROFILE', 'UPDATE_USER_SKILLS_SUCCESS', {}, data, null);
        } else {
          pushLog2DV2LogServer.warn('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, { code: response.data.code });
        }
      }
    } catch (e) {
      pushLog2DV2LogServer.error('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, e);
    }
  }

  deleteSkillData = async (allSkills, skillId) => {
    try {
      const { user_demand_stack_skills } = this.props;
      const response = await axios.post(`${ApiConstant.BASE_URL}${ApiConstant.DELETE_USER_SKILL_DATA}`, {
        id: skillId
      });
      if (response.data.code === CodeConstant.OK) {
        const data = {
          allSkills: allSkills.slice()
        };

        const otherSkills = allSkills.filter(skill => !user_demand_stack_skills.skillsIds.includes(skill.skill_id)).map((skill) => skill.skill_id);

        if (this.props.handleAllSkills) {
          this.props.handleAllSkills(data, true);
        }

        if (this.state.unmounted) {
          this.setState({ ...data });
          this.props.setUserOtherSkills(otherSkills);
        }
        pushLog2DV2LogServer.info('USER_PROFILE', 'UPDATE_USER_SKILLS_SUCCESS', {}, data, null);
      } else {
        pushLog2DV2LogServer.warn('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, { code: response.data.code });
      }
    } catch (e) {
      pushLog2DV2LogServer.error('USER_PROFILE', 'UPDATE_USER_SKILLS_FAILED', {}, {}, e);
    }
  }

  render () {
    let { allSkills=[] } = this.state;
    const { baseSkills, newSkill } = this.state;
    const { showEr, validation_error, user_demand_stack_skills, LDFlags } = this.props;
    const { use_onboarding_improved_ui_ux_multi_variant } = LDFlags;

    const usedSkills = [];
    allSkills.map(skill => {
      usedSkills[skill.skill_id] = true;
    });

    allSkills = allSkills.sort((a, b) => {
      let value_a, value_b;
      if (a.is_ready_to_match) { value_a = 2; } else if (user_demand_stack_skills.skillsIds.includes(a.skill_id)) { value_a = 1; } else value_a = 0;

      if (b.is_ready_to_match) { value_b = 2; } else if (user_demand_stack_skills.skillsIds.includes(b.skill_id)) { value_b = 1; } else value_b = 0;
      return value_b - value_a;
    });

    let i = 0;
    const formItems = allSkills.map((item, index) => {
      i++;
      const id = item && item.id ? (item.id).toString() : undefined;
      const skill_id = item && item.skill_id ? (item.skill_id).toString() : undefined;
      const skill_years = item && item.score ? (item.score).toString() : undefined;
      const skill_level = item && item.skill_level ? SKILL_LEVELS[item.skill_level] : undefined;
      const skill_name = item && item.skill_name ? item.skill_name : undefined;
      const closable = skill_id !== undefined && skill_years !== undefined && skill_level !== undefined;
      let tag = '';
      let tagMobile = '';
      if (item.is_ready_to_match) {
        tag = 'Already RTM';
        tagMobile = 'RTM';
      } else if (user_demand_stack_skills.skillsIds.includes(item.skill_id)) {
        tag = 'Required';
        tagMobile = 'Req.';
      }

      // Append current skill in all list (baseSkills) to show even if not in canonical skills list
      let appendedBaseSkills = baseSkills;
      const skillInBase = baseSkills.filter((skill) => skill.id === item.skill_id).length > 0;
      appendedBaseSkills = skillInBase ? baseSkills : [...baseSkills, item];

      return <tr key={i} style={{ height: '56px' }}>
        <td className="skill-info">
          <Select
            style={{ height: '40px' }}
            placeholder="Enter skill name to search"
            showSearch
            onSelect={(value) => this.handleAllSkillInfoChange(id, 'skill_id', value)}
            value={skill_name}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={!!tag}
          >
            {appendedBaseSkills.map((skill) => {
              if (usedSkills[skill.id]) {
                return (<Option key={skill.id} value={skill.id} disabled>{skill.skill_name}</Option>);
              } else return (<Option key={skill.id} value={skill.id}>{skill.skill_name}</Option>);
            })}
          </Select>
        </td>
        <td className="skill-info">
          <InputNumber min={1} max={99}
            size="large"
            maxLength="2"
            onChange={(value) => this.handleAllSkillInfoChange(id, 'score', value)}
            onBlur = {this.saveAllSkillUpdatedExperience}
            onKeyDown={this.validateSkillExperienceInput}
            value={skill_years}
            style={{ width: '100%' }}
          />
        </td>
            {
              !use_onboarding_improved_ui_ux_multi_variant ||  use_onboarding_improved_ui_ux_multi_variant === 'v1' ? (
                <td className="skill-info">
                  <Select
                    style={{ height: '40px' }}
                    onSelect={(value) => this.handleAllSkillInfoChange(id, 'skill_level', value)}
                    value={skill_level}
                  >
                    {Object.keys(SKILL_LEVELS).map((key) => (
                      <Option key={key} value={key}>
                        <div>
                          <div>
                            <b>{SKILL_LEVELS[key]}</b>
                          </div>
                          <div style={{ color: '#888888', whiteSpace: 'normal' }}>
                            {levelMapping[SKILL_LEVELS[key].toLowerCase()]}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </td>
              ) : null
            }
        <td className="skill-tag-row">
          {tag
            ? <>
              <Alert message={tag} type="success" className="badge skill-tag-message"/>
              <Alert message={tagMobile} type="success" className="badge skill-tag-message-mobile"/>
            </>
            : (
              (i !== 0 || closable)
                ? (
                  <CancelIcon style={{ color: '#888' }} onClick={() => this.handleDeleteAllSkill(id)}/>
                )
                : null
            )}
        </td>
      </tr>;
    });

    return (
      <div>
        {/* 9th DEC. NOTE: THIS SKILL EDITOR UI IS BAD ON MOBILE/SMALL SCREEN - PLZ FIX IN THE NEXT SPRINT */}
        <div className="section-part">
          <div className="part-header">
            {!this.props.hideTitle && (
              <Text className={'part-title' + (showEr && this.stillGotError ? ' title-error' : '')}>What skills do you have? </Text>
            )}
            {showEr && this.stillGotError && <Text style={{ color: '#F86567', marginLeft: '15px' }}><HeaderErrorIcon/><Text style={{ color: '#F86567', marginLeft: '7px' }}>{validation_error.message}</Text></Text>}
          </div>
          {
            use_onboarding_improved_ui_ux_multi_variant === 'v1' ? (
              <Text className="part-description">Add your skills, years of experience and how competent you are with each of them.</Text>
            ) : null
          }
          {
            use_onboarding_improved_ui_ux_multi_variant === 'v2' ? (
              <Text className="part-description">Add your skills and years of experience with each of them.</Text>
            ) : null
          }
          <div className="part-body">
            <table className={`skill-list ${use_onboarding_improved_ui_ux_multi_variant === 'v2' ? 'expand-width' : ''}`}>
              <thead>
                <tr>
                  <th className="skill-info">Skill</th>
                  <th className="skill-info">Years of Professional Experience</th>
                   {!use_onboarding_improved_ui_ux_multi_variant ||  use_onboarding_improved_ui_ux_multi_variant === 'v1' ? <th className="skill-info">Competency</th> : null}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                { formItems }
                <tr key="addSkill" style={{ height: '56px' }}>
                  <td className="skill-info">
                    <Select
                      style={{ height: '40px' }}
                      placeholder="Enter skill name to search"
                      showSearch
                      onSelect={(value) => this.handleAddAllSkill('skill_id', value)}
                      value={newSkill.skill_name}
                      filterOption={(input, option) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {baseSkills.map((skill) => {
                        if (usedSkills[skill.id]) {
                          return (<Option key={skill.id} value={skill.id} disabled>{skill.skill_name}</Option>);
                        } else return (<Option key={skill.id} value={skill.id}>{skill.skill_name}</Option>);
                      })}
                    </Select>
                  </td>
                  <td className="skill-info">
                    <InputNumber min={1} max={99}
                      size="large"
                      maxLength="2"
                      onChange={(value) => this.handleAddAllSkill('score', value)}
                      onKeyDown={this.validateSkillExperienceInput}
                      value={newSkill.score}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {
                    !use_onboarding_improved_ui_ux_multi_variant ||  use_onboarding_improved_ui_ux_multi_variant === 'v1' ? (
                      <td className="skill-info">
                        <Select
                          style={{ height: '40px' }}
                          onSelect={(value) => this.handleAddAllSkill('skill_level', value)}
                          value={newSkill.skill_level}
                        >
                          {Object.keys(SKILL_LEVELS).map((key) => (
                            <Option key={key} value={key}>
                              <div>
                                <div>
                                  <b>{SKILL_LEVELS[key]}</b>
                                </div>
                                <div style={{ color: '#888888', whiteSpace: 'normal' }}>
                                  {levelMapping[SKILL_LEVELS[key].toLowerCase()]}
                                </div>
                              </div>
                            </Option>
                          ))}
                        </Select>
                      </td>
                    ) : null
                  }
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}

SkillEditor.propTypes = {
  // userId: PropTypes.number.isRequired,
  handleAllSkills: PropTypes.func,
  hideTitle: PropTypes.bool,
  user_demand_stack_skills: PropTypes.object,
  user_other_skills: PropTypes.array,
  validation_error: PropTypes.object,
  setUserOtherSkills: PropTypes.func,
  setValidationError: PropTypes.func,
  onReady: PropTypes.func,
  showEr: PropTypes.bool
};

SkillEditor.defaultProps = {
  user_demand_stack_skills: {
    skillsIds: []
  }
};

const mapStateToProps = (store) => ({
  user_demand_stack_skills: store.user_demand_stack_skills,
  user_other_skills: store.skillEditor.user_other_skills,
  validation_error: store.skillEditor.validation_error
});

const mapDispatchToProps = (dispatch) => ({
  setUserOtherSkills: skills => dispatch(setUserOtherSkills(skills)),
  setValidationError: error => dispatch(setValidationError(error))
});

export default connect(mapStateToProps, mapDispatchToProps)(withLDFlagsFromBackend(SkillEditor, [GB_FLAGS.skill_dedup_skill_selection, GB_FLAGS.use_onboarding_improved_ui_ux_multi_variant]));